import type { FC } from 'react';
import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { marketingApi } from '../../api/marketing';

const PaymentPage: FC = () => {
  const [seatCount, setSeatCount] = useState(1);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pricing tiers
  const getPricePerSeat = (seats: number) => {
    if (seats <= 5) return 99;
    if (seats <= 20) return 79;
    return 59;
  };

  const pricePerSeat = getPricePerSeat(seatCount);
  const discount = billingCycle === 'annual' ? 0.2 : 0;
  const effectivePrice = pricePerSeat * (1 - discount);
  const monthlyTotal = effectivePrice * seatCount;
  const annualTotal = monthlyTotal * 12;

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await marketingApi.createCheckout(seatCount, billingCycle);
      // Redirect to Stripe Checkout
      window.location.href = response.checkoutUrl;
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    'Full VoIP softphone integration',
    'FDCPA & TCPA compliance tracking',
    'Account management & payment plans',
    'Reporting & analytics',
    'Email & SMS communications',
    'Dedicated support for Enterprise plans',
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Choose Your Plan
        </Typography>
        <Typography color="text.secondary">
          Select the number of user seats and billing cycle for your team.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Configuration Card */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Seat Count */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Number of User Seats
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Each user who needs to log in requires a seat
                </Typography>
                <TextField
                  type="number"
                  value={seatCount}
                  onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1, max: 999 }}
                  sx={{ width: 120 }}
                />
                <Box sx={{ mt: 2 }}>
                  {seatCount <= 5 && (
                    <Chip label="Starter Tier - $99/seat" color="primary" size="small" />
                  )}
                  {seatCount > 5 && seatCount <= 20 && (
                    <Chip label="Professional Tier - $79/seat" color="primary" size="small" />
                  )}
                  {seatCount > 20 && (
                    <Chip label="Enterprise Tier - $59/seat" color="primary" size="small" />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Billing Cycle */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Billing Cycle
                </Typography>
                <ToggleButtonGroup
                  value={billingCycle}
                  exclusive
                  onChange={(_, value) => value && setBillingCycle(value)}
                  fullWidth
                >
                  <ToggleButton value="monthly">
                    <Box sx={{ py: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Monthly
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay as you go
                      </Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton value="annual">
                    <Box sx={{ py: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Annual
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save 20%
                      </Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Features List */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Included Features
                </Typography>
                <List dense>
                  {features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Card */}
        <Grid item xs={12} md={5}>
          <Card sx={{ position: 'sticky', top: 100 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>

              <Box sx={{ my: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">
                    {seatCount} seat{seatCount > 1 ? 's' : ''} × ${pricePerSeat}/mo
                  </Typography>
                  <Typography>
                    ${pricePerSeat * seatCount}/mo
                  </Typography>
                </Box>
                {billingCycle === 'annual' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="success.main">
                      Annual discount (20%)
                    </Typography>
                    <Typography color="success.main">
                      -${(pricePerSeat * seatCount * 0.2).toFixed(0)}/mo
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">
                  Monthly Total
                </Typography>
                <Typography variant="h6">
                  ${monthlyTotal.toFixed(0)}/mo
                </Typography>
              </Box>

              {billingCycle === 'annual' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography color="text.secondary">
                    Billed annually
                  </Typography>
                  <Typography color="text.secondary">
                    ${annualTotal.toFixed(0)}/yr
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                Debtornator launches Q3 2027. Trial terms will be confirmed at launch.
              </Alert>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleCheckout}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
                sx={{ mb: 2 }}
              >
                {loading ? 'Processing...' : 'Continue'}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Secure checkout powered by Stripe
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentPage;
